import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProtectContext } from "../../context/protect_context";
import { IMAGE_BASE_URL } from "../../utils/image_url";
import { setPageTitle } from "../../utils/page_title";
import type { BannerData, RegularData, SectionWithDatas } from "../../types";
import { isBannerData, isRegularData, isSectionWithDatas } from "../../types";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { data } = React.useContext(ProtectContext);
  
  // Get advices from context (dynamic data)
  const newsData = (data?.advices || []).map((advice) => ({
    id: advice.id,
    title: advice.title,
    image: advice.image ? `${IMAGE_BASE_URL}${advice.image}` : "/no-image.png",
    videoUrl: advice.videoUrl || null,
    desc: advice.desc || null,
  }));

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 400;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Get home page data (first item in datas array)
  const homePageData = data?.datas?.[0];

  // Get hero slides from sectionId 1
  const heroSection = homePageData?.sections?.find(
    (s): s is SectionWithDatas => isSectionWithDatas(s) && s.sectionId === 1
  );
  const heroSlides: BannerData[] =
    heroSection?.datas?.filter(isBannerData) || [];

  // Logic to determine the layout:
  // 1. Find a "Main" slide that has both a title and a URL (priority), or default to the first one.
  const mainSlide = heroSlides.find((s) => s.title && s.url) || heroSlides[0];

  // 2. Get the remaining slides to show as smaller secondary items (max 2).
  const secondarySlides = heroSlides.filter((s) => s !== mainSlide).slice(0, 2);

  // Get readiness steps from sectionId 2
  const readinessSection = homePageData?.sections?.find(
    (s): s is SectionWithDatas => isSectionWithDatas(s) && s.sectionId === 2
  );
  const readinessSteps: RegularData[] =
    readinessSection?.datas?.filter(
      (d): d is RegularData => isRegularData(d) && !d.isHeader
    ) || [];
  const headerData = readinessSection?.datas?.find(
    (d): d is RegularData => isRegularData(d) && d.isHeader
  );

  // Set page title
  useEffect(() => {
    setPageTitle(homePageData || null, data);
  }, [homePageData, data]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Hero Section - Modern Design */}
      <section className="relative overflow-hidden px-4 py-12 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
            {/* Main Hero Card */}
            {mainSlide && (
              <div className="group relative col-span-1 min-h-[500px] overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-2xl transition-all duration-700 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] lg:col-span-8 lg:min-h-[600px]">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-all duration-1000 group-hover:scale-110"
                  style={{
                    backgroundImage: `url(${
                      mainSlide.banner
                        ? `${IMAGE_BASE_URL}${mainSlide.banner}`
                        : ""
                    })`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/40" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent" />

                {/* Glassmorphism overlay */}
                <div className="absolute inset-0 backdrop-blur-[0.5px]" />

                <div className="relative flex h-full flex-col justify-end p-8 lg:p-12">
                  {mainSlide.title && (
                    <div className="mb-6 space-y-4">
                      <h2 className="max-w-3xl text-3xl font-extrabold leading-tight text-white drop-shadow-2xl lg:text-5xl xl:text-6xl">
                        {mainSlide.title
                          .split("<br />")
                          .map((line: string, i: number) => (
                            <span
                              key={i}
                              className="block animate-fade-in-up"
                              style={{ animationDelay: `${i * 0.1}s` }}
                            >
                              {line}
                            </span>
                          ))}
                      </h2>
                    </div>
                  )}
                  {mainSlide.url && (
                    <a
                      href={
                        mainSlide.url
                          ? `${IMAGE_BASE_URL}${mainSlide.url}`
                          : "#"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/btn w-fit relative inline-flex items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-red-500 to-orange-500 px-8 py-4 text-base font-bold text-white shadow-lg shadow-red-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-red-500/60"
                    >
                      <span className="relative z-10 flex items-center gap-3">
                        <svg
                          className="h-6 w-6 transition-transform duration-300 group-hover/btn:scale-110"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5.14v13.72L19 12 8 5.14z" />
                        </svg>
                        Бичлэг үзэх
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 transition-opacity duration-300 group-hover/btn:opacity-100" />
                    </a>
                  )}
                </div>

                {/* Decorative elements */}
                <div className="absolute right-8 top-8 hidden h-32 w-32 rounded-full bg-gradient-to-br from-red-500/20 to-orange-500/20 blur-3xl lg:block" />
                <div className="absolute bottom-20 right-20 hidden h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-2xl lg:block" />
              </div>
            )}

            {/* Secondary Cards */}
            <div className="col-span-1 flex flex-col gap-6 lg:col-span-4">
              {secondarySlides.map((slide, index) => (
                <div
                  key={index}
                  className="group relative min-h-[280px] flex-1 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{
                      backgroundImage: `url(${
                        slide.banner ? `${IMAGE_BASE_URL}${slide.banner}` : ""
                      })`,
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
                  <div className="absolute inset-0 backdrop-blur-[0.5px]" />

                  <div className="relative flex h-full flex-col justify-end p-6">
                    {slide.title && (
                      <h3 className="text-xl font-bold leading-tight text-white drop-shadow-lg lg:text-2xl">
                        {slide.title.replace(/<br \/>/g, " ")}
                      </h3>
                    )}
                  </div>

                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-orange-500/0 opacity-0 transition-opacity duration-500 group-hover:from-red-500/10 group-hover:to-orange-500/10 group-hover:opacity-100" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* News Section - Modern Carousel */}
      {newsData.length > 0 && <section className="relative overflow-hidden py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-12 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-red-50 to-orange-50 px-4 py-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                <span className="text-xs font-semibold uppercase tracking-wider text-red-600">
                  Зөвлөмж
                </span>
              </div>
              <h3 className="text-4xl font-extrabold tracking-tight text-slate-900 lg:text-5xl">
                Урьдчилан сэргийлэх зөвлөмж
              </h3>
              <p className="max-w-2xl text-lg text-slate-600 lg:text-xl">
                Гамшигаас урьдчилан сэргийлэх зөвлөмж
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => scroll("left")}
                className="group flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-slate-200 bg-white text-slate-700 shadow-lg transition-all duration-300 hover:border-red-400 hover:bg-gradient-to-br hover:from-red-50 hover:to-orange-50 hover:shadow-xl hover:shadow-red-500/20"
                aria-label="Previous"
              >
                <svg
                  className="h-6 w-6 transition-transform duration-300 group-hover:-translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <button
                onClick={() => scroll("right")}
                className="group flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-slate-200 bg-white text-slate-700 shadow-lg transition-all duration-300 hover:border-red-400 hover:bg-gradient-to-br hover:from-red-50 hover:to-orange-50 hover:shadow-xl hover:shadow-red-500/20"
                aria-label="Next"
              >
                <svg
                  className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable News Cards */}
        {newsData.length > 0 && <div
          ref={scrollContainerRef}
          className="flex gap-8 overflow-x-auto pb-8 scrollbar-hide px-4 lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))]"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {newsData.length === 0 ? (
            <div className="flex items-center justify-center h-0 w-full">
              {/* <p className="text-gray-500 dark:text-gray-400">Зөвлөмж байхгүй байна</p> */}
            </div>
          ) : (
            newsData.map((news, index) => (
              <div
                onClick={() => {
                  navigate(`/news/${news.id}`, { state: { news } });
                }}
                key={news.id || index}
                className="group relative h-[450px] w-[320px] shrink-0 cursor-pointer overflow-hidden rounded-3xl bg-white shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl sm:h-[500px] sm:w-[380px] lg:h-[600px] lg:w-[500px]"
              >
              <div className="absolute inset-0 overflow-hidden rounded-3xl">
                <img
                  src={news.image}
                  alt={news.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent" />

              {/* Content */}
              <div className="absolute inset-x-0 bottom-0 p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 backdrop-blur-md">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white">
                    Зөвлөмж
                  </span>
                </div>
                <h4 className="text-2xl font-bold leading-tight text-white drop-shadow-lg transition-transform duration-300 group-hover:translate-x-2 lg:text-3xl">
                  {news.title}
                </h4>

                {/* Hover Arrow */}
                <div className="mt-6 flex items-center gap-2 text-white opacity-0 transition-all duration-300 group-hover:opacity-100">
                  <span className="text-sm font-semibold">Дэлгэрэнгүй</span>
                  <svg
                    className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-all duration-1000 group-hover:translate-x-full group-hover:opacity-100" />
              </div>
            ))
          )}
        </div>}
      </section>}

      {/* Readiness Steps Section - Modern Cards */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-white px-4 py-16 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-20">
            {/* Left Column - Header */}
            <div className="space-y-8">
              {headerData?.category && (
                <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-red-50 to-orange-50 px-5 py-2.5">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-red-600">
                    {headerData.category}
                  </p>
                </div>
              )}

              {headerData?.title && (
                <h3 className="text-4xl font-extrabold leading-tight text-slate-900 lg:text-5xl">
                  {headerData.title}
                </h3>
              )}

              {headerData?.desc && (
                <div
                  className="prose prose-lg max-w-none space-y-4 text-slate-600 prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-red-600"
                  dangerouslySetInnerHTML={{ __html: headerData.desc }}
                />
              )}

              {headerData?.link && headerData?.btnTitle && (
                <Link
                  to={headerData.link}
                  className="group relative inline-flex items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-red-500 to-orange-500 px-8 py-4 text-base font-bold text-white shadow-lg shadow-red-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-red-500/60"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                >
                  <span className="relative z-10">{headerData.btnTitle}</span>
                  <svg
                    className="relative z-10 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="M12 5l7 7-7 7" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </Link>
              )}
            </div>

            {/* Right Column - Steps */}
            <div className="space-y-6">
              {readinessSteps.map((step: RegularData, index: number) => (
                <article
                  key={index}
                  className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:border-red-300 hover:shadow-2xl hover:shadow-red-500/10"
                >
                  {/* Gradient Background on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-red-50/0 to-orange-50/0 transition-all duration-500 group-hover:from-red-50/50 group-hover:to-orange-50/50" />

                  <div className="relative flex items-start gap-6">
                    {/* Number Badge */}
                    <div className="relative">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 text-2xl font-extrabold text-white shadow-lg shadow-red-500/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-400 to-orange-400 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-50" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 space-y-4">
                      <h4 className="text-xl font-bold text-slate-900 transition-colors duration-300 group-hover:text-red-600 lg:text-2xl">
                        {step.title}
                      </h4>
                      {step.desc && (
                        <p className="text-base leading-relaxed text-slate-600">
                          {step.desc}
                        </p>
                      )}
                      {step.link && step.btnTitle && (
                        <Link
                          to={step.link}
                          className="inline-flex items-center gap-2 text-base font-semibold text-red-600 transition-all duration-300 hover:gap-3 hover:text-red-700"
                          onClick={() =>
                            window.scrollTo({ top: 0, behavior: "smooth" })
                          }
                        >
                          {step.btnTitle}
                          <svg
                            className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13 7l5 5m0 0l-5 5m5-5H6"
                            />
                          </svg>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Decorative corner element */}
                  <div className="absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-gradient-to-br from-red-100/0 to-orange-100/0 blur-3xl transition-all duration-500 group-hover:from-red-100/50 group-hover:to-orange-100/50" />
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
